/**
 * Modal — accessible dialog rendered into document.body via React portal.
 *
 * Uses `.modal-scrim` and `.modal` classes from app-shell.css.
 * Closes on scrim click or Escape keypress. The scrim is always visible
 * when open (no `.show` class toggling needed — the component mounts/unmounts).
 *
 * Usage:
 *   <Modal open={isOpen} onClose={() => setOpen(false)} title="New project">
 *     <p>Dialog content here</p>
 *   </Modal>
 */

import { type ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/Icon";

export type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
};

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  width,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  // Captured during the closed→open render — by the time effects run, an
  // autoFocus field inside the dialog has already taken focus, so reading
  // document.activeElement there would "restore" to the dialog's own input.
  const triggerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  if (open && !wasOpenRef.current) {
    triggerRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
  }
  wasOpenRef.current = open;

  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Move focus into the dialog on open and back to the trigger on close —
  // keyboard/screen-reader users otherwise stayed parked on the (now
  // scrim-covered) button that opened it (review finding).
  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) {
      const target =
        dialog.querySelector<HTMLElement>(
          "input, textarea, select, button:not(.mx), [href], [tabindex]:not([tabindex='-1'])",
        ) ?? dialog;
      target.focus();
    }
    return () => triggerRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      // `.show` flips display:grid via app-shell.css. Without it the scrim
      // (and the modal it centres) stay display:none even when mounted.
      className="modal-scrim show portal"
      data-testid="modal-scrim"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={width !== undefined ? { width } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <h3 className="modal-title">{title}</h3>
          <button
            type="button"
            className="mx"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="close" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
